import React from 'react';
import { Check, Clock, FileText, CheckCircle, ClipboardList, Target, Upload, BadgeCheck, Banknote, Users, Rocket, Inbox } from 'lucide-react';

/**
 * Enhanced Campaign Progress Indicator Component
 * Professional, animated progress indicator for campaign stages
 */
const CampaignProgressIndicator = ({ currentStage, userType = 'influencer' }) => {
  // Define stages for influencers (with pre-approval workflow)
  const influencerStages = [
    { id: 'applied', label: 'Applied', icon: FileText, description: 'Application submitted' },
    { id: 'approved', label: 'Approved', icon: CheckCircle, description: 'Application accepted' },
    { id: 'draft_submitted', label: 'Draft Submitted', icon: ClipboardList, description: 'Content draft sent for review' },
    { id: 'draft_approved', label: 'Draft Approved', icon: Target, description: 'Content approved for posting' },
    { id: 'proof_submitted', label: 'Proof Submitted', icon: Upload, description: 'Proof of posting submitted' },
    { id: 'content_approved', label: 'Content Approved', icon: BadgeCheck, description: 'Final content approved' },
    { id: 'paid', label: 'Paid', icon: Banknote, description: 'Payment processed' }
  ];

  // Define stages for brands
  const brandStages = [
    { id: 'created', label: 'Created', icon: ClipboardList, description: 'Campaign created' },
    { id: 'reviewing_apps', label: 'Review Apps', icon: Users, description: 'Reviewing applications' },
    { id: 'active', label: 'Active', icon: Rocket, description: 'Campaign is live' },
    { id: 'reviewing_content', label: 'Review Content', icon: Inbox, description: 'Reviewing submissions' },
    { id: 'completed', label: 'Completed', icon: CheckCircle, description: 'Campaign finished' }
  ];

  const stages = userType === 'influencer' ? influencerStages : brandStages;

  // Map current stage to status
  const getStageStatus = (stageId, index) => {
    const currentIndex = stages.findIndex(s => s.id === currentStage);
    if (index < currentIndex) return 'complete';
    if (index === currentIndex) return 'current';
    return 'upcoming';
  };

  // Update stages with actual status
  const stagesWithStatus = stages.map((stage, index) => ({
    ...stage,
    status: getStageStatus(stage.id, index)
  }));

  const getStageStyles = (status, index, stage) => {
    const baseStyles = "relative flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-500 ease-in-out transform";
    const StageIcon = stage?.icon;
    
    switch (status) {
      case 'complete':
        return {
          circle: `${baseStyles} bg-gradient-to-br from-green-400 to-green-600 border-green-500 shadow-lg scale-100 hover:scale-105`,
          text: 'text-green-700 font-semibold',
          connector: 'bg-gradient-to-r from-green-400 to-green-500',
          icon: <Check className="w-6 h-6 text-white" />,
          glow: 'shadow-green-200'
        };
      case 'current':
        return {
          circle: `${baseStyles} bg-gradient-to-br from-blue-500 to-purple-600 border-blue-500 shadow-xl scale-110 animate-pulse ring-4 ring-blue-200 ring-opacity-50`,
          text: 'text-blue-700 font-bold',
          connector: 'bg-gradient-to-r from-gray-300 to-gray-400',
          icon: StageIcon ? <StageIcon className="w-6 h-6 text-white" /> : <Clock className="w-6 h-6 text-white" />,
          glow: 'shadow-blue-300'
        };
      case 'upcoming':
        return {
          circle: `${baseStyles} bg-gray-100 border-gray-300 shadow-sm scale-95 hover:scale-100 hover:bg-gray-200`,
          text: 'text-gray-500 font-medium',
          connector: 'bg-gray-300',
          icon: StageIcon ? <StageIcon className="w-5 h-5 text-gray-400" /> : <span className="text-gray-400 text-lg font-bold">{index + 1}</span>,
          glow: 'shadow-gray-100'
        };
      default:
        return {
          circle: `${baseStyles} bg-gray-100 border-gray-300`,
          text: 'text-gray-500',
          connector: 'bg-gray-300',
          icon: StageIcon ? <StageIcon className="w-5 h-5 text-gray-400" /> : <span className="text-gray-400">{index + 1}</span>,
          glow: 'shadow-gray-100'
        };
    }
  };

  return (
    <div className="w-full py-8">
      {/* Desktop View */}
      <div className="hidden md:block">
        <div className="relative">
          {/* Progress Line Background */}
          <div className="absolute top-6 left-6 right-6 h-1 bg-gray-200 rounded-full"></div>
          
          {/* Animated Progress Line */}
          <div 
            className="absolute top-6 left-6 h-1 bg-gradient-to-r from-green-400 via-blue-500 to-purple-600 rounded-full transition-all duration-1000 ease-out"
            style={{ 
              width: `${Math.max(0, (stagesWithStatus.findIndex(s => s.status === 'current') / (stagesWithStatus.length - 1)) * 100)}%` 
            }}
          ></div>
          
          {/* Stage Items */}
          <div className="relative flex justify-between items-start">
            {stagesWithStatus.map((stage, index) => {
              const styles = getStageStyles(stage.status, index, stage);
              const isLast = index === stagesWithStatus.length - 1;

              return (
                <div key={stage.id} className="flex flex-col items-center group cursor-pointer" style={{ flex: '1' }}>
                  {/* Stage Circle with Animation */}
                  <div className={`${styles.circle} ${styles.glow} group-hover:shadow-2xl`}>
                    {styles.icon}
                    
                    {/* Pulse Animation for Current Stage */}
                    {stage.status === 'current' && (
                      <div className="absolute inset-0 rounded-full bg-blue-400 opacity-20 animate-ping"></div>
                    )}
                  </div>
                  
                  {/* Stage Label and Description */}
                  <div className="mt-3 text-center max-w-24">
                    <p className={`text-xs ${styles.text} transition-colors duration-300 group-hover:text-blue-600`}>
                      {stage.label}
                    </p>
                    {stage.status === 'current' && (
                      <p className="text-xs text-blue-500 mt-1 font-medium animate-pulse">In Progress</p>
                    )}
                    {stage.status === 'complete' && (
                      <p className="text-xs text-green-600 mt-1 font-medium flex items-center justify-center gap-0.5">
                        <Check className="h-3 w-3" /> Complete
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile View */}
      <div className="md:hidden">
        <div className="space-y-4">
          {stagesWithStatus.map((stage, index) => {
            const styles = getStageStyles(stage.status, index, stage);
            const isLast = index === stagesWithStatus.length - 1;

            return (
              <div key={stage.id} className="flex items-start group">
                {/* Left side - icon and line */}
                <div className="flex flex-col items-center mr-4">
                  <div className={`w-10 h-10 ${styles.circle.replace('w-12 h-12', 'w-10 h-10')} ${styles.glow}`}>
                    {React.cloneElement(styles.icon, { 
                      className: styles.icon.props.className?.replace('w-6 h-6', 'w-5 h-5')?.replace('w-5 h-5', 'w-5 h-5')
                    })}
                    
                    {/* Pulse Animation for Current Stage */}
                    {stage.status === 'current' && (
                      <div className="absolute inset-0 rounded-full bg-blue-400 opacity-20 animate-ping"></div>
                    )}
                  </div>
                  {!isLast && (
                    <div className={`w-1 h-12 mt-2 rounded-full transition-all duration-500 ${
                      stage.status === 'complete' ? 'bg-gradient-to-b from-green-400 to-green-600' : 'bg-gray-300'
                    }`} />
                  )}
                </div>

                {/* Right side - content */}
                <div className="flex-1">
                  <h4 className={`text-sm font-medium ${styles.text} transition-colors duration-300`}>
                    {stage.label}
                  </h4>
                  <p className="text-xs text-gray-500 mt-1">
                    {stage.description}
                  </p>
                  {stage.status === 'current' && (
                    <p className="text-xs text-blue-500 mt-1 font-medium animate-pulse">In Progress</p>
                  )}
                  {stage.status === 'complete' && (
                    <p className="text-xs text-green-600 mt-1 font-medium flex items-center gap-0.5">
                      <Check className="h-3 w-3" /> Complete
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CampaignProgressIndicator;
